import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-lodash-debounce';

import { usePhoneOptionsContext } from '../../../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../types/api';
import { ClientLookupCollection } from '../../../../../../types/api/clients';
import { HubCollection } from '../../../../../../types/api/hubs';
import { PlacesStreetCollection } from '../../../../../../types/api/places';
import { ReduxState } from '../../../../../../types/redux';
import {
  debounceTimeout,
  isStreetFreeInputAllowed,
} from '../../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../../utils/constants/phoneNumbers';
import { queryString } from '../../../../../../utils/helpers/http';
import { tryString } from '../../../../../../utils/helpers/parse';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../../utils/helpers/phoneNumbers';
import AutoCompleteInput from '../../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import { PlaceCollection, isNumber } from '../CreateEditDialog.functions';

type Props = {
  hubs: HubCollection | undefined;
  orderIndex?: number;
  isWarehouseReception?: boolean;
  isConstant: boolean;
  isSenderTabDisabled: boolean;
};

function SenderTab({
  hubs,
  orderIndex = 0,
  isWarehouseReception,
  isConstant,
  isSenderTabDisabled,
}: Props) {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const values = watch();

  const debouncedClient = useDebounce(values.klient_od_ime, debounceTimeout);

  const user = useSelector<ReduxState, ReduxState['user']>(
    (state) => state.user
  );

  const employeeHub = hubs?.data.find((hub) => {
    return hub.id == user.employee_hub_id;
  });

  const hubsSuggestions =
    hubs?.data.map((hub) => {
      return { id: hub.id, label: hub.name };
    }) ?? [];

  const { data: clientsData, isLoading: isClientDataLoading } =
    useAxiosHook<ClientLookupCollection>(
      '/clients/lookup' +
        queryString({
          ime: !isNumber(debouncedClient ?? '') ? debouncedClient : null,
          telefon: isNumber(debouncedClient ?? '') ? debouncedClient : null,
          page: 1,
          limit: 10,
        }),
      { skipWhen: !!values?.no_sender || !!isSenderTabDisabled }
    );

  const clientsSuggestions =
    clientsData?.data?.map((client) => {
      return { label: client.ime, value: String(client.id) };
    }) ?? [];

  const { data: places, isLoading: isPlacesLoading } =
    useAxiosHook<PlaceCollection>(`/places?limit=0`, { skipCache: true });

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxiosHook<WithData<PlacesStreetCollection>>(
    `/places/${values?.pickup_address.placeId}/streets?limit=0`,
    {
      skipWhen: !values?.pickup_address.placeId,
      skipCache: true,
    }
  );

  const placesOptions = useMemo(
    () =>
      places && places?.data.length > 0
        ? places?.data.map((m: any) => ({
            label: m.name,
            value: m.id,
          }))
        : [],
    [places]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets, values?.pickup_address.streetId]
  );

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const handleClientSelect = useCallback(
    (id: string | null) => {
      setValue('klient_od_id', id);

      const sender = clientsData?.data.find(
        (sender) => String(sender.id) === id
      );

      setValue('telefon_od', sender?.telefon ?? '');
      setValue(
        'telefon_od_region',
        getPhoneorMobileNumberRegion(sender?.telefon ?? '')
      );
      setValue('mobilen_od', sender?.mobilen ?? '');
      setValue(
        'mobilen_od_region',
        getPhoneorMobileNumberRegion(sender?.mobilen ?? '')
      );
      setValue('pickup_address', sender?.address ?? '');
    },
    [clientsData?.data, setValue]
  );

  const handleClientChange = useCallback(
    (ime: string) => {
      setValue('klient_od_ime', ime);
    },
    [setValue]
  );

  return (
    <div>
      {orderIndex !== 0 && isConstant && <div className="constant" />}

      {values?.no_sender ? (
        <FieldWithErrors name="klient_od_ime" label={t('Sender Name')}>
          <Controller
            name="klient_od_ime"
            render={({ field }) => (
              <InputText
                id="klient_od_ime"
                name="klient_od_ime"
                value={field.value}
                onChange={field.onChange}
                placeholder={t('Required')}
                disabled={isSenderTabDisabled}
              />
            )}
          />
        </FieldWithErrors>
      ) : (
        <FieldWithErrors name="klient_od_ime" label={t('Search Clients')}>
          <AutoCompleteInput
            disabled={isSenderTabDisabled}
            placeholder={
              isClientDataLoading
                ? t('Loading...')
                : t('Search by name or phone number')
            }
            options={clientsSuggestions}
            value={tryString(values.klient_od_id) ?? null}
            filterValue={values.klient_od_ime ?? ''}
            onFilterChange={handleClientChange}
            onSelectionChange={handleClientSelect}
            filterDataCy="sender"
            optionsClassName="data-cy-sender-options"
          />
        </FieldWithErrors>
      )}

      <FieldWithErrors
        name="no_sender"
        label={false}
        className="p-d-flex p-mb-2 p-jc-between"
      >
        <Controller
          name="no_sender"
          render={({ field }) => (
            <>
              <Checkbox
                id="no_sender"
                name="no_sender"
                value={field.value}
                checked={field.value}
                onChange={(e) => {
                  if (e.checked) {
                    setValue('klient_od_id', '1');
                    setValue('no_sender', true);
                  } else {
                    setValue('klient_od_id', '');
                    setValue('no_sender', false);
                  }
                }}
                disabled={isWarehouseReception || isSenderTabDisabled}
              />
              <label htmlFor="no_sender" data-cy="no_sender">
                {t('Unknown client')}
              </label>
            </>
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors name="pickup_address.placeId" label={t('Place')}>
        <Controller
          name="pickup_address.placeId"
          render={({ field }) => (
            <Dropdown
              {...field}
              id="pickup_address.placeId"
              name="pickup_address.placeId"
              options={placesOptions}
              value={field.value}
              onChange={(e) => {
                if (e.value) {
                  field.onChange(e.value);
                  const item = placesOptions?.find((s) => s.value === e.value);
                  setValue('pickup_address.placeName', item?.label);

                  reloadStreets({
                    url: `places/${e.value}/streets?limit=0`,
                  });
                } else {
                  setValue('pickup_address.placeId', null);
                  setValue('pickup_address.placeName', '');
                  setValue('pickup_address.streetId', null);
                  setValue('pickup_address.streetName', '');
                  setValue('pickup_address.housenumber', '');
                  setValue('pickup_address.entrance', '');
                  setValue('pickup_address.apartment', '');
                }
              }}
              filter
              filterPlaceholder={t('Search')}
              placeholder={
                isPlacesLoading ? t('Loading...') : t('Search places')
              }
              disabled={isPlacesLoading || isSenderTabDisabled}
              className="data-cy-placeId"
              optionLabel="label"
              optionValue="value"
            />
          )}
        />
      </FieldWithErrors>

      {isStreetFreeInputAllowed ? (
        <FieldWithErrors name="pickup_address.rawName" label={t('Address')}>
          <Controller
            name="pickup_address.rawName"
            render={({ field }) => (
              <InputText
                id="pickup_address.rawName"
                name="pickup_address.rawName"
                value={field.value}
                onChange={field.onChange}
                disabled={!values?.pickup_address.placeName || isStreetsLoading}
              />
            )}
          />
        </FieldWithErrors>
      ) : (
        <>
          <FieldWithErrors name="pickup_address.streetId" label={t('Street')}>
            <Controller
              name="pickup_address.streetId"
              render={({ field }) => (
                <Dropdown
                  id="pickup_address.streetId"
                  name="pickup_address.streetId"
                  options={streetsOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const item = streetsOptions?.find(
                        (s) => s.value === e.value
                      );
                      setValue('pickup_address.streetName', item?.label);
                    } else {
                      setValue('pickup_address.streetId', null);
                      setValue('pickup_address.streetName', '');
                      setValue('pickup_address.housenumber', '');
                      setValue('pickup_address.entrance', '');
                      setValue('pickup_address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={
                    isStreetsLoading ? t('Loading...') : t('Select Street')
                  }
                  disabled={
                    !values?.pickup_address.placeId ||
                    isStreetsLoading ||
                    isSenderTabDisabled
                  }
                  className="data-cy-streetId"
                />
              )}
            />
          </FieldWithErrors>

          <div className="p-grid">
            <FieldWithErrors
              name="pickup_address.housenumber"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="pickup_address.housenumber"
                render={({ field }) => (
                  <InputText
                    id="pickup_address.housenumber"
                    name="pickup_address.housenumber"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values?.pickup_address.streetId}
                    placeholder={t('Street No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors
              name="pickup_address.entrance"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="pickup_address.entrance"
                render={({ field }) => (
                  <InputText
                    id="pickup_address.entrance"
                    name="pickup_address.entrance"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values?.pickup_address.streetId}
                    placeholder={t('Entrance No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors
              name="pickup_address.apartment"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="pickup_address.apartment"
                render={({ field }) => (
                  <InputText
                    id="pickup_address.apartment"
                    name="pickup_address.apartment"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values?.pickup_address.streetId}
                    placeholder={t('Flat No.')}
                  />
                )}
              />
            </FieldWithErrors>
          </div>
        </>
      )}

      <FieldWithErrors name="mobilen_od" label={t('Mobile')}>
        <div className="p-inputgroup number-group">
          <Controller
            name="mobilen_od_region"
            render={({ field }) => (
              <Dropdown
                style={{ padding: 0, width: '5rem' }}
                id="mobilen_od_region"
                options={languages}
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                data-cy="mobilen_od_region"
                {...field}
              />
            )}
          />
          <Controller
            name="mobilen_od"
            render={({ field }) => (
              <InputText
                name="mobilen_od"
                style={{ width: '100%' }}
                value={formatMobileNumber(
                  values?.mobilen_od,
                  values?.mobilen_od_region ||
                    getPhoneorMobileNumberRegion(values?.mobilen_od)
                )}
                onChange={(e) => {
                  field.onChange(
                    e.target.value?.replace(
                      invalidPhoneNumberCharactersRegex,
                      ''
                    )
                  );
                }}
                className="data-cy-mobile"
              />
            )}
          />
        </div>
      </FieldWithErrors>

      <FieldWithErrors name="telefon_od" label={t('Phone')}>
        <div className="p-inputgroup">
          <Controller
            name="telefon_od_region"
            render={({ field }) => (
              <Dropdown
                style={{ padding: 0, width: '5rem' }}
                id="telefon_od_region"
                options={languages}
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                data-cy="telefon_od_region"
                {...field}
              />
            )}
          />
          <Controller
            name="telefon_od"
            render={({ field }) => (
              <InputText
                name="telefon_od"
                style={{ width: '100%' }}
                value={formatPhoneNumber(
                  values?.telefon_od,
                  values?.telefon_od_region ||
                    getPhoneorMobileNumberRegion(values?.telefon_od)
                )}
                onChange={(e) => {
                  field.onChange(
                    e.target.value?.replace(
                      invalidPhoneNumberCharactersRegex,
                      ''
                    )
                  );
                }}
                className="data-cy-telefon"
              />
            )}
          />
        </div>
      </FieldWithErrors>

      <FieldWithErrors name="pickup_hub" label={t('Handover in warehouse')}>
        <div className="p-d-flex p-ai-center">
          <Controller
            name="pickup_location_type_id"
            render={({ field }) => (
              <Checkbox
                id="pickup_location_type_id"
                name="pickup_location_type_id"
                value={field.value}
                checked={field.value}
                onChange={(e) => {
                  setValue('pickup_location_type_id', e.checked);

                  if (!e.checked) {
                    setValue('hub_od_id', null);
                    return;
                  }
                  setValue('hub_od_id', employeeHub?.id ?? t('Unknown'));
                }}
                disabled={isWarehouseReception || isSenderTabDisabled}
                className="hub-checkbox"
              />
            )}
          />
          <Controller
            name="hub_od_id"
            render={({ field }) => (
              <Dropdown
                id="hub_od_id"
                name="hub_od_id"
                options={hubsSuggestions}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                filter
                filterPlaceholder={t('Search')}
                disabled={!values?.pickup_location_type_id}
                className="data-cy-hub_od_id"
              />
            )}
          />
        </div>
      </FieldWithErrors>
    </div>
  );
}

export default SenderTab;
